import { type ColumnDef } from "@tanstack/react-table";

import { Button } from "@/components/ui";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { DataTableColumnHeader } from "@/components/ui/shadcn/DataTable/DataTableColumnHeader";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import type { MedicalTreatmentBillViewModel } from "@/hooks/matter-view-models/use-medical-treatment-bill-view-model";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { MatterTrustCheck } from "@/lib/types";
import { formatDate, trimText } from "@/lib/utils/format";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { createColumnHelper } from "@tanstack/react-table";
import { DollarSign, Sparkles } from "lucide-react";

// Component for the trust check button
const TrustCheckButton = ({
  matterId,
  medicalTreatmentBillId,
}: {
  matterId: string;
  medicalTreatmentBillId: string;
}) => {
  const { openTrustCheckModal } = useGlobalModal();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    openTrustCheckModal({
      matterId,
      trustCheckType: "Withdraw",
      editing: false,
      medicalTreatmentBillId: medicalTreatmentBillId,
    });
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleClick}
      className="h-8 w-8"
    >
      <DollarSign className="h-4 w-4" />
    </Button>
  );
};

const columnHelper = createColumnHelper<MedicalTreatmentBillViewModel>();

const isAiGeneratedBill = (bill: MedicalTreatmentBillViewModel) =>
  Boolean(bill.aiStatus || bill.aiAffectedFields?.length);

export const matterTreatmentBillsColumns: ColumnDef<MedicalTreatmentBillViewModel>[] =
  [
    columnHelper.accessor("dateCreated", {
      id: "Date Created",
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => (
        <div>
          {formatDate(convertToLocalFromDatabaseDateTime(row.getValue()))}
        </div>
      ),
      size: 120,
    }),
    columnHelper.accessor("dateModified", {
      id: "Date Modified",
      sortingFn: "customDateTime",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => <div>{formatDate(row.getValue()) ?? "-"}</div>,
      size: 120,
    }),
    columnHelper.accessor("recordStatus", {
      id: "Record Status",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => <div>{row.getValue()}</div>,
      size: 120,
    }),
    columnHelper.accessor("recordLabel", {
      id: "Bill Name",
      enableHiding: false,
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const value = row.original.recordLabel;
        const showAiIndicator = isAiGeneratedBill(row.original);

        return (
          <div className="flex items-center gap-2">
            <span>{trimText(value) || "-"}</span>
            {showAiIndicator ? (
              <TooltipWrapper message="AI generated bill">
                <span className="inline-flex" aria-label="AI generated bill">
                  <Sparkles className="h-4 w-4 text-blue-500" />
                </span>
              </TooltipWrapper>
            ) : null}
          </div>
        );
      },
      size: 160,
    }),
    columnHelper.accessor("recordRequestDate", {
      id: "Record Request Date",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        return <div>{value ? formatDate(value) : "-"}</div>;
      },
      size: 120,
    }),
    columnHelper.accessor("recordReceivedDate", {
      id: "Record Received Date",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        return <div>{value ? formatDate(value) : "-"}</div>;
      },
      size: 120,
    }),
    columnHelper.accessor("originalBill", {
      id: "Original Bill",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        if (typeof value === "undefined" || value === null) {
          return <div>-</div>;
        }
        return <CurrencyWrapper value={value} />;
      },
      size: 120,
    }),
    columnHelper.accessor("adjustedBill", {
      id: "Adjusted Bill",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        if (value === undefined || value === null || Number(value) === 0) {
          return <div>-</div>;
        }
        return <CurrencyWrapper value={value} />;
      },
      size: 120,
    }),
    columnHelper.accessor("negotiatedAmount", {
      id: "Negotiated Bill",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        if (value === undefined || value === null || Number(value) === 0) {
          return <div>-</div>;
        }
        return <CurrencyWrapper value={value} />;
      },
      size: 120,
    }),
    columnHelper.accessor("clientPaid", {
      id: "Client Paid",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        if (typeof value === "undefined" || value === null) {
          return <div>-</div>;
        }
        return <CurrencyWrapper value={value} />;
      },
      size: 120,
    }),
    columnHelper.accessor("insurancePaid", {
      id: "Insurance Paid",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        if (typeof value === "undefined" || value === null) {
          return <div>-</div>;
        }
        return <CurrencyWrapper value={value} />;
      },
      size: 120,
    }),
    columnHelper.accessor("medpayPipPaid", {
      id: "Medpay/PIP Paid",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        if (typeof value === "undefined" || value === null) {
          return <div>-</div>;
        }
        return <CurrencyWrapper value={value} />;
      },
      size: 120,
    }),
    columnHelper.accessor("totalPaid", {
      id: "Total Paid",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const { totalPaid } = calculateMedicalTreatmentBillTotals([
          {
            originalBill: row.original.originalBill,
            adjustedBill: row.original.adjustedBill,
            clientPaid: row.original.clientPaid,
            insurancePaid: row.original.insurancePaid,
            medpayPipPaid: row.original.medpayPipPaid,
          },
        ]);
        if (totalPaid === 0) {
          return <div>-</div>;
        }
        return <CurrencyWrapper value={totalPaid} />;
      },
      size: 120,
    }),
    columnHelper.accessor("remainingBalance", {
      id: "Outstanding Balance",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const { totalOutstanding } = calculateMedicalTreatmentBillTotals([
          {
            originalBill: row.original.originalBill,
            adjustedBill: row.original.adjustedBill,
            clientPaid: row.original.clientPaid,
            insurancePaid: row.original.insurancePaid,
            medpayPipPaid: row.original.medpayPipPaid,
          },
        ]);
        if (totalOutstanding === 0) {
          return <div>-</div>;
        }
        return <CurrencyWrapper value={totalOutstanding} />;
      },
      size: 120,
    }),
    // columnHelper.accessor("totalPaidPlusOwed", {
    //   id: "Total Paid + Owed",
    //   header: ({ column }) => <DataTableColumnHeader column={column} />,
    //   cell: (row) => {
    //     const value = row.getValue();
    //     if (typeof value === "undefined" || value === null) {
    //       return <div>-</div>;
    //     }
    //     return <CurrencyWrapper value={value} />;
    //   },
    //   size: 150,
    // }),
    columnHelper.accessor("description", {
      id: "Description",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => <div>{row.getValue() ?? "-"}</div>,
      size: 200,
    }),
    columnHelper.accessor("fileNumber", {
      id: "File Number",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => <div>{row.getValue() ?? "-"}</div>,
      size: 120,
    }),
    columnHelper.accessor("letterOfProtection", {
      id: "Letter of Protection",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        if (!value) {
          return <div>-</div>;
        }
        return <div>{value}</div>;
      },
      size: 100,
    }),
    columnHelper.accessor("lien", {
      id: "Lien",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: (row) => {
        const value = row.getValue();
        if (!value) {
          return <div>-</div>;
        }
        return <div>{value}</div>;
      },
      size: 100,
    }),
    columnHelper.accessor("trustChecks", {
      id: "Trust Checks",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const trustChecks = row.original.trustChecks as
          | MatterTrustCheck[]
          | undefined;
        return <span>{trustChecks?.length ?? "-"}</span>;
      },
    }),
    columnHelper.accessor("matterId", {
      id: "Actions",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      cell: ({ row }) => {
        const matterId = row.original.matterId;
        const medicalTreatmentBillId = row.original.id;
        return matterId ? (
          <TrustCheckButton
            matterId={matterId}
            medicalTreatmentBillId={medicalTreatmentBillId}
          />
        ) : null;
      },
    }),
  ] as ColumnDef<MedicalTreatmentBillViewModel>[];

// Function to create columns with additional fields when on settlements page
export const createMatterTreatmentBillsColumns = (
  isOnSettlementsPage: boolean = false,
  isOnLiensPage: boolean = false,
): ColumnDef<MedicalTreatmentBillViewModel>[] => {
  // Add goal amount, negotiated amount, and reduction fee agreement columns to the end
  const columns = [...matterTreatmentBillsColumns];

  /* Settlements Page Columns */
  const goalAmountColumn: ColumnDef<MedicalTreatmentBillViewModel> = {
    id: "Goal Amount",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    accessorKey: "goalAmount",
    cell: ({ row }) => {
      const value = row.original.goalAmount;
      if (typeof value === "undefined" || value === null) {
        return <div>-</div>;
      }
      return <CurrencyWrapper value={value} />;
    },
    size: 120,
  };

  const negotiatedAmountColumn: ColumnDef<MedicalTreatmentBillViewModel> = {
    id: "Negotiated Amount",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    accessorKey: "negotiatedAmount",
    cell: ({ row }) => {
      const value = row.original.negotiatedAmount;
      if (typeof value === "undefined" || value === null) {
        return <div>-</div>;
      }
      return <CurrencyWrapper value={value} />;
    },
    size: 120,
  };

  const reductionFeeAgreementColumn: ColumnDef<MedicalTreatmentBillViewModel> =
    {
      id: "Reduction Fee Agreement",
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      accessorKey: "reductionFeeAgreement",
      cell: ({ row }) => {
        const value = row.original.reductionFeeAgreement;
        if (value === undefined) {
          return <div>-</div>;
        }
        return <div>{value ? "Yes" : "No"}</div>;
      },
      size: 100,
    };

  /* Liens Page Columns */
  const providerColumn: ColumnDef<MedicalTreatmentBillViewModel> = {
    id: "Provider",
    header: ({ column }) => <DataTableColumnHeader column={column} />,
    accessorKey: "provider",
    cell: ({ row }) => {
      const value =
        getConnectionContactName(row.original.provider?.contact ?? undefined) ??
        "Provider not assigned";
      return <div>{value}</div>;
    },
    size: 120,
  };

  if (isOnSettlementsPage) {
    columns.push(
      goalAmountColumn,
      negotiatedAmountColumn,
      reductionFeeAgreementColumn,
    );
  }

  if (isOnLiensPage) {
    columns.push(providerColumn);
  }

  return columns;
};
